import { useEffect, useMemo, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  Plus,
  CalendarRange,
  History,
  Archive,
  LayoutGrid,
  List,
  Package2,
  XCircle, ChevronRight } from 'lucide-react';
import { PageHeader } from '@/components/ui/PageHeader';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Chip } from '@/components/ui/Chip';
import { EmptyState } from '@/components/ui/EmptyState';
import { SearchInput } from '@/components/ui/SearchInput';
import { Segmented } from '@/components/ui/Segmented';
import { Table, THead, TBody, TR, TH, TD } from '@/components/ui/Table';
import { EventCard, EVENT_STATUS_CHIP, EVENT_STATUS_LABEL } from '@/components/events/EventCard';
import { EventActionMenu } from '@/components/events/EventActionMenu';
import { EventForm } from '@/components/events/EventForm';
import { CloseEventDialog } from '@/components/events/CloseEventDialog';
import {
  useActiveEvents,
  useArchivedEvents,
  useEventHistory,
  useArchiveEvent,
  useUnarchiveEvent,
  useReopenEvent,
  useVoidEvent,
} from '@/hooks/useEvents';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { useActiveEventStore } from '@/hooks/useActiveEvent';
import { usePermissions } from '@/hooks/usePermissions';
import { eventColorClasses } from '@/lib/eventColors';
import { cn, formatDateTime, formatIDR, formatNumber } from '@/lib/format';
import type { AppEvent } from '@/types/domain';

type Tab = 'active' | 'history' | 'archived';
type View = 'card' | 'list';

const VIEW_KEY = 'ophth_events_view';

function readView(): View {
  try {
    return localStorage.getItem(VIEW_KEY) === 'list' ? 'list' : 'card';
  } catch {
    return 'card';
  }
}

export default function Events() {
  const navigate = useNavigate();
  const { can } = usePermissions();
  const canManage = can('events', 'edit');
  // Cashiers must not see all-event sales totals.
  const showMoney = can('reports', 'view');
  const setActiveEvent = useActiveEventStore((s) => s.setActiveEvent);
  const activeEventId = useActiveEventStore((s) => s.activeEventId);

  const [tab, setTab] = useState<Tab>('active');
  const [view, setView] = useState<View>(readView);
  const [search, setSearch] = useState('');

  useEffect(() => {
    try {
      localStorage.setItem(VIEW_KEY, view);
    } catch { /* private browsing: the choice just does not persist */ }
  }, [view]);

  const [formOpen, setFormOpen] = useState(false);
  const [closeTarget, setCloseTarget] = useState<AppEvent | null>(null);
  const [editTarget, setEditTarget] = useState<AppEvent | null>(null);

  // One slot per confirmable action. They are separate rather than a single
  // {action, event} because each has its own wording, and a shared slot made
  // the dialog flash the previous action's text while closing.
  const [archiveTarget, setArchiveTarget] = useState<AppEvent | null>(null);
  const [unarchiveTarget, setUnarchiveTarget] = useState<AppEvent | null>(null);
  const [reopenTarget, setReopenTarget] = useState<AppEvent | null>(null);
  const [voidTarget, setVoidTarget] = useState<AppEvent | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const { data: activeEvents, isLoading: loadingActive } = useActiveEvents();
  const { data: historyEvents, isLoading: loadingHistory } = useEventHistory();
  const { data: archivedEvents, isLoading: loadingArchived } = useArchivedEvents();

  const archiveMut = useArchiveEvent();
  const unarchiveMut = useUnarchiveEvent();
  const reopenMut = useReopenEvent();
  const voidMut = useVoidEvent();

  const currentList =
    tab === 'active' ? activeEvents : tab === 'history' ? historyEvents : archivedEvents;
  const isLoading =
    tab === 'active' ? loadingActive : tab === 'history' ? loadingHistory : loadingArchived;

  /**
   * The database is the authority on whether a lifecycle change is allowed —
   * void refuses an event with sales, reopen refuses a voided one. Surfacing its
   * message verbatim is deliberate: it names the event and the reason, which is
   * more use than anything this component could reword.
   */
  const runAction = async (fn: () => Promise<unknown>, done: () => void) => {
    setActionError(null);
    try {
      await fn();
      done();
    } catch (e) {
      setActionError(e instanceof Error ? e.message : 'Gagal memproses event');
    }
  };

  const filtered = useMemo(() => {
    const list = currentList ?? [];
    if (!search.trim()) return list;
    const s = search.toLowerCase();
    return list.filter(
      (e) =>
        e.name.toLowerCase().includes(s) ||
        e.code.toLowerCase().includes(s) ||
        (e.location?.toLowerCase().includes(s) ?? false),
    );
  }, [currentList, search]);

  const activeCount = activeEvents?.length ?? 0;
  const historyCount = historyEvents?.length ?? 0;

  const handleSelect = (evt: AppEvent) => {
    setActiveEvent(evt.id, evt.name);
  };

  // Per-event handler set, shared by the card and the list row so the two
  // views cannot drift in what they allow.
  const handlersFor = (evt: AppEvent) => ({
    onSelect: tab === 'active' ? handleSelect : undefined,
    onManageStock: (e: AppEvent) => navigate(`/events/${e.id}?tab=stok`),
    onClose: tab === 'active' ? (e: AppEvent) => setCloseTarget(e) : undefined,
    onEdit: canManage ? (e: AppEvent) => setEditTarget(e) : undefined,
    onArchive: canManage ? (e: AppEvent) => setArchiveTarget(e) : undefined,
    onUnarchive: canManage ? (e: AppEvent) => setUnarchiveTarget(e) : undefined,
    onReopen: canManage ? (e: AppEvent) => setReopenTarget(e) : undefined,
    onVoid: canManage ? (e: AppEvent) => setVoidTarget(e) : undefined,
    isActive: evt.id === activeEventId,
  });

  return (
    <>
      <PageHeader
        title="Events"
        description="Kelola lifecycle event — start, monitor, dan close. Multiple events bisa berjalan concurrent."
        actions={
          canManage && (
            <Button variant="primary" leftIcon={<Plus size={16} />} onClick={() => setFormOpen(true)}>
              Start event
            </Button>
          )
        }
      />

      {/* Money totals are owner-only. A cashier may not read what every event
          has taken; the counts are workload, not money, and stay. */}
      <div className={cn('grid gap-4 sm:grid-cols-2 mb-6', showMoney && 'lg:grid-cols-4')}>
        <Card padding="md">
          <p className="text-overline uppercase text-ink-subtle font-medium">Active events</p>
          <p className="text-metric font-display font-semibold mt-2 text-ink">{activeCount}</p>
        </Card>
        <Card padding="md">
          <p className="text-overline uppercase text-ink-subtle font-medium">Closed (history)</p>
          <p className="text-metric font-display font-semibold mt-2 text-ink">{historyCount}</p>
        </Card>
        {showMoney && (
          <>
            <Card padding="md">
              <p className="text-overline uppercase text-ink-subtle font-medium">Total penjualan (active)</p>
              <p className="text-metric font-display font-semibold mt-2 text-ink">
                {formatIDR((activeEvents ?? []).reduce((s, e) => s + e.totalSales, 0))}
              </p>
            </Card>
            <Card padding="md">
              <p className="text-overline uppercase text-ink-subtle font-medium">Transaksi (active)</p>
              <p className="text-metric font-display font-semibold mt-2 text-ink">
                {formatNumber((activeEvents ?? []).reduce((s, e) => s + e.totalTransactions, 0))}
              </p>
            </Card>
          </>
        )}
      </div>

      <div className="mb-4 flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
        <Segmented<Tab>
          value={tab}
          onChange={setTab}
          aria-label="Status event"
          options={[
            { value: 'active', label: `Active (${activeCount})`, icon: <CalendarRange size={14} /> },
            { value: 'history', label: `History (${historyCount})`, icon: <History size={14} /> },
            { value: 'archived', label: `Arsip (${archivedEvents?.length ?? 0})`, icon: <Archive size={14} /> },
          ]}
        />
        <div className="flex items-center gap-2 w-full sm:w-auto">
          <div className="flex-1 sm:w-64">
            <SearchInput value={search} onChange={setSearch} placeholder="Cari nama / code / lokasi…" />
          </div>
          <Segmented<View>
            value={view}
            onChange={setView}
            role="radio"
            size="sm"
            aria-label="Tampilan"
            options={[
              { value: 'card', label: <span className="sr-only">Kartu</span>, icon: <LayoutGrid size={14} />, title: 'Tampilan kartu' },
              { value: 'list', label: <span className="sr-only">Daftar</span>, icon: <List size={14} />, title: 'Tampilan daftar' },
            ]}
          />
        </div>
      </div>

      {isLoading ? (
        <Card padding="lg">
          <p className="text-center text-ink-subtle text-body">
            Loading {tab === 'active' ? 'active events' : 'history'}…
          </p>
        </Card>
      ) : filtered.length === 0 ? (
        <Card padding="lg">
          <EmptyState
            icon={tab === 'active' ? <CalendarRange size={24} /> : <History size={24} />}
            title={
              search
                ? 'Tidak ada event yang cocok'
                : tab === 'active'
                  ? 'Belum ada event aktif'
                  : 'Belum ada history event'
            }
            description={
              search
                ? 'Coba kata kunci lain atau hapus filter.'
                : tab === 'active'
                  ? 'Mulai event pertama untuk mulai berjualan di POS.'
                  : 'Event yang ditutup akan muncul di sini sebagai history.'
            }
            action={
              !search && tab === 'active' && canManage ? (
                <Button variant="primary" leftIcon={<Plus size={16} />} onClick={() => setFormOpen(true)}>
                  Start event pertama
                </Button>
              ) : undefined
            }
          />
        </Card>
      ) : view === 'card' ? (
        <div data-tour="events-list" className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {filtered.map((evt) => (
            <EventCard
              key={evt.id}
              event={evt}
              canClose={canManage}
              showMoney={showMoney}
              {...handlersFor(evt)}
            />
          ))}
        </div>
      ) : (
        <Card padding="none" data-tour="events-list">
          <Table>
            <THead>
              <TR>
                <TH>Event</TH>
                <TH>Status</TH>
                <TH>Mulai</TH>
                <TH>Lokasi</TH>
                <TH className="text-right">Transaksi</TH>
                {showMoney && <TH className="text-right">Penjualan</TH>}
                <TH className="text-right">Aksi</TH>
              </TR>
            </THead>
            <TBody>
              {filtered.map((evt) => {
                const h = handlersFor(evt);
                const color = eventColorClasses(evt.color);
                const isOpen = evt.status === 'active';
                return (
                  <TR key={evt.id} className={cn(h.isActive && 'bg-primary/5')}>
                    <TD className="relative">
                      {/* Same accent as the card: a colour bar on the row's edge and a tinted code pill. */}
                      {color.set && <span className={cn('absolute inset-y-0 left-0 w-1.5', color.dot)} aria-hidden />}
                      <div className="flex items-center gap-2.5 min-w-0">
                        <span className={cn('w-3 h-3 rounded-full shrink-0', color.dot)} aria-hidden />
                        <div className="min-w-0">
                          <Link to={`/events/${evt.id}`} className="font-medium text-ink truncate hover:underline block">{evt.name}</Link>
                          <code
                            className={cn(
                              'mt-0.5 inline-block rounded-full border px-2 py-px text-caption font-mono',
                              color.set ? cn(color.soft, color.text) : 'border-transparent px-0 text-ink-subtle',
                            )}
                          >
                            {evt.code}
                          </code>
                        </div>
                      </div>
                    </TD>
                    <TD>
                      <Chip variant={EVENT_STATUS_CHIP[evt.status]}>{EVENT_STATUS_LABEL[evt.status]}</Chip>
                    </TD>
                    <TD className="text-small text-ink-subtle whitespace-nowrap">{formatDateTime(evt.startAt)}</TD>
                    <TD className="text-small text-ink-subtle max-w-[220px] truncate" title={evt.location}>
                      {evt.location || '—'}
                    </TD>
                    <TD className="text-right tabular-nums">{formatNumber(evt.totalTransactions)}</TD>
                    {showMoney && <TD className="text-right tabular-nums">{formatIDR(evt.totalSales)}</TD>}
                    <TD>
                      <div className="flex justify-end items-center gap-1">
                        {h.onSelect && isOpen && (
                          <Button
                            variant={h.isActive ? 'primary' : 'secondary'}
                            size="sm"
                            onClick={() => h.onSelect!(evt)}
                          >
                            {h.isActive ? 'Aktif' : 'Pilih'}
                          </Button>
                        )}
                        <Button variant="secondary" size="sm" onClick={() => navigate(`/events/${evt.id}`)} rightIcon={<ChevronRight size={14} />}>
                          Detail
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => h.onManageStock(evt)} title="Stok event" aria-label="Stok event">
                          <Package2 size={16} className="text-ink-subtle" />
                        </Button>
                        {isOpen && canManage && h.onClose && (
                          <Button variant="ghost" size="sm" onClick={() => h.onClose!(evt)} title="Close event" aria-label="Close event">
                            <XCircle size={16} className="text-danger" />
                          </Button>
                        )}
                        <EventActionMenu
                          event={evt}
                          onEdit={h.onEdit}
                          onArchive={h.onArchive}
                          onUnarchive={h.onUnarchive}
                          onReopen={h.onReopen}
                          onVoid={h.onVoid}
                        />
                      </div>
                    </TD>
                  </TR>
                );
              })}
            </TBody>
          </Table>
        </Card>
      )}

      <EventForm open={formOpen} onClose={() => setFormOpen(false)} />
      {editTarget && (
        <EventForm open editingEventId={editTarget.id} onClose={() => setEditTarget(null)} />
      )}

      {actionError && (
        <div
          role="alert"
          className="fixed bottom-4 right-4 z-50 max-w-md p-3 rounded border border-danger/30 bg-danger/10 text-small text-danger shadow-lg"
        >
          {actionError}
          <button type="button" className="ml-3 underline" onClick={() => setActionError(null)}>
            tutup
          </button>
        </div>
      )}

      <ConfirmDialog
        open={!!archiveTarget}
        title="Arsipkan event?"
        description={`"${archiveTarget?.name ?? ''}" disembunyikan dari daftar kerja. Datanya tetap utuh dan bisa dikembalikan kapan saja.`}
        confirmText="Arsipkan"
        loading={archiveMut.isPending}
        onClose={() => setArchiveTarget(null)}
        onConfirm={() => runAction(() => archiveMut.mutateAsync(archiveTarget!.id), () => setArchiveTarget(null))}
      />

      <ConfirmDialog
        open={!!unarchiveTarget}
        title="Kembalikan dari arsip?"
        description={`"${unarchiveTarget?.name ?? ''}" akan kembali berstatus aktif dan bisa dipakai berjualan lagi.`}
        confirmText="Kembalikan"
        loading={unarchiveMut.isPending}
        onClose={() => setUnarchiveTarget(null)}
        onConfirm={() => runAction(() => unarchiveMut.mutateAsync(unarchiveTarget!.id), () => setUnarchiveTarget(null))}
      />

      <ConfirmDialog
        open={!!reopenTarget}
        title="Buka kembali event?"
        description={`"${reopenTarget?.name ?? ''}" kembali aktif dan bisa menerima transaksi. Catatan qty kembali gudang dari penutupan sebelumnya akan dinolkan, karena barangnya berarti keluar lagi ke event.`}
        confirmText="Buka kembali"
        loading={reopenMut.isPending}
        onClose={() => setReopenTarget(null)}
        onConfirm={() => runAction(() => reopenMut.mutateAsync(reopenTarget!.id), () => setReopenTarget(null))}
      />

      <ConfirmDialog
        open={!!voidTarget}
        title="Void event ini?"
        description={`"${voidTarget?.name ?? ''}" ditandai sebagai event yang seharusnya tidak pernah ada. Tidak bisa dibuka kembali. Hanya boleh untuk event yang belum punya transaksi sama sekali.`}
        confirmText="Void"
        variant="danger"
        loading={voidMut.isPending}
        onClose={() => setVoidTarget(null)}
        onConfirm={() => runAction(() => voidMut.mutateAsync({ id: voidTarget!.id }), () => setVoidTarget(null))}
      />

      <CloseEventDialog
        eventId={closeTarget?.id ?? null}
        open={!!closeTarget}
        onClose={() => setCloseTarget(null)}
      />
    </>
  );
}
